import { useState } from 'react'
import type { CodeSampleTab, CodeTone } from '@/features/home/data/codeSamples'
import { codeSampleTabs } from '@/features/home/data/codeSamples'
import { cn } from '@/lib/utils'

const TONE_CLASS: Record<CodeTone, string> = {
  plain: 'text-[var(--color-text-primary)]',
  keyword: 'text-primary',
  string: 'text-accent',
  comment: 'text-[var(--color-text-tertiary)] italic',
  muted: 'text-[var(--color-text-tertiary)]',
}

interface CodeSampleProps {
  /** Defaults to the quickstart tabs; /agents passes its own. */
  tabs?: Array<CodeSampleTab>
}

/** Tabbed quickstart snippet with the single line that changes called out. */
export function CodeSample({ tabs = codeSampleTabs }: CodeSampleProps) {
  const [activeId, setActiveId] = useState(tabs[0].id)
  const active = tabs.find((t) => t.id === activeId) ?? tabs[0]

  return (
    <div className="surface-bevel overflow-hidden rounded-[1.75rem]">
      <div
        role="tablist"
        aria-label="Language"
        className="flex items-center gap-1 border-b border-[var(--color-border)] px-3 py-2.5"
      >
        {tabs.map((tab) => {
          const selected = tab.id === active.id
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setActiveId(tab.id)}
              className={cn(
                'rounded-full px-3.5 py-1.5 text-body-sm font-medium transition-all duration-300 ease-brisk active:scale-[0.97] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary/50',
                selected
                  ? 'border border-[var(--color-border-strong)] bg-[var(--color-control-surface-hover)] text-[var(--color-text-primary)]'
                  : 'border border-transparent text-[var(--color-text-tertiary)] hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)]',
              )}
            >
              {tab.label}
            </button>
          )
        })}
        <span className="ml-auto hidden font-mono text-caption text-[var(--color-text-tertiary)] sm:inline">
          {active.id === 'curl'
            ? 'bash'
            : `main.${active.id === 'python' ? 'py' : 'ts'}`}
        </span>
      </div>

      <pre className="overflow-x-auto bg-[var(--color-bg-primary)]/50 p-6 font-mono text-body-sm leading-7">
        <code>
          {active.lines.map((line, i) => (
            <div
              key={i}
              className={cn(
                'flex whitespace-pre',
                line.highlight &&
                  '-mx-6 border-l-2 border-primary bg-primary/10 px-6 pl-[calc(1.5rem-2px)]',
              )}
            >
              <span className="mr-4 w-4 shrink-0 select-none text-right text-[var(--color-text-tertiary)]/70">
                {i + 1}
              </span>
              <span>
                {line.segments.length === 0 ? ' ' : null}
                {line.segments.map((seg, j) => (
                  <span key={j} className={TONE_CLASS[seg.tone ?? 'plain']}>
                    {seg.text}
                  </span>
                ))}
              </span>
            </div>
          ))}
        </code>
      </pre>
    </div>
  )
}
